import {
	Alert,
	Dialog,
	DialogContent,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableRow,
} from "@mui/material";
import { useEffect, useState } from "react";
import AddOutgoingNumber from "../add-outgoing-number/add-outgoing-number";
import ButtonAddNew from "src/components/atoms/button-add-new/button-add-new";
import { ReactComponent as DeleteIcon } from "../../../assets/delete.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/download.svg";
import TableHeader from "src/components/molecules/table-header/table-header";
import {
	IFileInfo,
	INewNumberModel,
	ServiceStatus,
} from "src/types/models/apiModels";
import { IBusinessNumbersProps } from "../numbers/numbers";
import PhoneNumberDisplay from "src/components/atoms/phone-number-display/phone-number-display";

const NewBusinessNumbers: React.FC<IBusinessNumbersProps> = ({
	editNumbers,
	onChange,
}) => {
	const [numberAddError, setNumberAddError] = useState("");
	const [showNumberAddingError, setShowNumberAddingError] = useState(false);
	const [businessNumbers, setBusinessNumbers] = useState<INewNumberModel[]>(
		editNumbers ?? []
	);
	const [addNumberDialogOpen, setAddNumberDialogOpen] =
		useState<boolean>(false);
	useEffect(() => {
		if (onChange) {
			onChange(businessNumbers);
		}
	}, [businessNumbers]);

	const addNumber = (number: string, fileInfo: IFileInfo) => {
		setShowNumberAddingError(false);
		setNumberAddError("");

		setBusinessNumbers((prevNumbers) => {
			// Check if the number already exists in the list
			const numberExists = prevNumbers.some(
				(num) => num.number === number
			);

			if (numberExists) {
				// console.log("Number already exists. Update rejected.");
				setShowNumberAddingError(true);
				setNumberAddError("Number already exists. Update rejected.");
				// You can add more logic here, like showing an error message
				return prevNumbers; // Return the existing numbers without adding
			}

			const updatedNumbers = [
				...prevNumbers,
				{
					number: number,
					ownershipDocuments: [fileInfo],
					status: "New" as ServiceStatus,
				},
			];

			return updatedNumbers;
		});

		setAddNumberDialogOpen(false);
	};

	const deleteNumber = (number: string) => {
		setBusinessNumbers((prevNumbers) => {
			const updatedNumbers = prevNumbers.filter(
				(num) => num.number !== number
			);
			return updatedNumbers; // Return the updated array
		});

		setAddNumberDialogOpen(false);
	};

	const handleAddNewNumberClick = () => {
		setAddNumberDialogOpen(true);
	};
	const toggleDialog = () => {
		setAddNumberDialogOpen((prevValue) => !prevValue);
	};

	return (
		<>
			<div className="font-Poppins flex flex-col items-center justify-center gap-4 w-full">
				<div className="flex items-center justify-start gap-2 w-full">
					<span className="font-Poppins font-medium text-2xl uppercase tracking-wider">
						Add Outgoing Numbers
					</span>
				</div>
			</div>
			{businessNumbers.length > 0 && (
				<Table sx={{ minWidth: 650, borderCollapse: "collapse" }}>
					<TableHeader
						headers={[
							"OUTGOING NO.",
							"PROOF OF OWNERSHIP",
							"STATUS",
							"ACTIONS",
						]}
					></TableHeader>
					<TableBody>
						{businessNumbers.map((row, index) => (
							<TableRow key={index}>
								<TableCell
									sx={{
										textAlign: "center",
										fontWeight: "bold",
									}}
								>
									<span
										className={`"text-gray-900" font-semibold text-lg`}
									>
										<PhoneNumberDisplay
											number={row.number}
											hasPlus={true}
										></PhoneNumberDisplay>
									</span>
								</TableCell>
								<TableCell
									sx={{
										textAlign: "center",
										fontWeight: "bold",
									}}
								>
									<IconButton size="small" color="primary">
										<DownloadIcon />
									</IconButton>
									{row.ownershipDocuments[0].fileName}
								</TableCell>
								<TableCell
									sx={{
										textAlign: "center",
										fontWeight: "bold",
									}}
								>
									{row.status}
								</TableCell>
								<TableCell sx={{ textAlign: "center" }}>
									<IconButton
										size="small"
										color="error"
										onClick={() => deleteNumber(row.number)}
									>
										<DeleteIcon />
									</IconButton>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			{showNumberAddingError && (
				<>
					<br></br>
					<Alert severity="error">{numberAddError}</Alert>
				</>
			)}

			<div className="flex items-center justify-end w-full gap-4">
				<ButtonAddNew
					disabled
					text="Import multiple numbers"
					onClick={handleAddNewNumberClick}
				></ButtonAddNew>
				<ButtonAddNew onClick={handleAddNewNumberClick}></ButtonAddNew>
			</div>
			<Dialog
				open={addNumberDialogOpen}
				onClose={toggleDialog}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						display: "flex", // Align content properly
						justifyContent: "center", // Center horizontally
						alignItems: "center", // Center vertically
						overflow: "hidden", // Prevent horizontal overflow
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<AddOutgoingNumber onAdd={addNumber} />
				</DialogContent>
			</Dialog>
		</>
	);
};

export default NewBusinessNumbers;
